import React, { useState } from 'react';
import { X, ArrowRight, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Generic "Set up this tool" modal — collects the required info to activate
 * a website-builder feature (Donations, Invoicing, Pay Links, etc.).
 *
 * Props:
 *   open · onClose · title · description · fields[]
 *   onSubmit(values) — async; close on success
 */
export default function SetupModal({ open, onClose, title, description, icon: Icon, fields = [], submitLabel = 'Save & Continue', onSubmit }) {
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);

  if (!open) return null;

  const submit = async () => {
    // basic required-field check
    const missing = fields.filter(f => f.required && !values[f.key]?.toString().trim());
    if (missing.length) {
      toast.error(`Required: ${missing.map(f => f.label).join(', ')}`);
      return;
    }
    setSaving(true);
    try {
      await onSubmit?.(values);
      toast.success('Saved');
      onClose();
    } catch (e) {
      toast.error(e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] bg-black/70 backdrop-blur-md flex items-center justify-center p-4" data-testid="setup-modal">
      <div className="w-full max-w-lg bg-[#15152E] border border-white/[0.08] rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.6)]">
        <div className="px-6 py-5 border-b border-white/[0.06] flex items-start gap-4">
          {Icon && (
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#8B5CF6] to-[#3B82F6] flex items-center justify-center shrink-0">
              <Icon size={22} className="text-white" />
            </div>
          )}
          <div className="flex-1">
            <h2 className="text-[18px] font-bold text-white font-syne">{title}</h2>
            {description && <p className="text-[12.5px] text-[#9CA3AF] mt-1 leading-relaxed">{description}</p>}
          </div>
          <button onClick={onClose} className="text-[#9CA3AF] hover:text-white p-1" data-testid="setup-close"><X size={16} /></button>
        </div>

        <div className="px-6 py-5 space-y-4 max-h-[60vh] overflow-y-auto">
          {fields.map((f) => (
            <div key={f.key}>
              <label className="block text-[10px] uppercase tracking-[0.18em] text-[#9CA3AF] font-bold mb-1.5">
                {f.label} {f.required && <span className="text-[#FCA5A5]">*</span>}
              </label>
              {f.hint && <p className="text-[11px] text-[#64748B] mb-2">{f.hint}</p>}
              {f.type === 'select' ? (
                <select
                  value={values[f.key] || ''}
                  onChange={(e) => setValues(v => ({ ...v, [f.key]: e.target.value }))}
                  className="w-full bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 py-2.5 text-[13px] text-white focus:outline-none focus:border-[#8B5CF6]/40"
                  data-testid={`setup-field-${f.key}`}
                >
                  <option value="">Select…</option>
                  {f.options?.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : f.type === 'textarea' ? (
                <textarea
                  value={values[f.key] || ''}
                  onChange={(e) => setValues(v => ({ ...v, [f.key]: e.target.value }))}
                  placeholder={f.placeholder}
                  rows={3}
                  className="w-full bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 py-2.5 text-[13px] text-white placeholder:text-[#64748B] focus:outline-none focus:border-[#8B5CF6]/40"
                  data-testid={`setup-field-${f.key}`}
                />
              ) : (
                <input
                  type={f.type || 'text'}
                  value={values[f.key] || ''}
                  onChange={(e) => setValues(v => ({ ...v, [f.key]: e.target.value }))}
                  placeholder={f.placeholder}
                  className="w-full bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 py-2.5 text-[13px] text-white placeholder:text-[#64748B] focus:outline-none focus:border-[#8B5CF6]/40"
                  data-testid={`setup-field-${f.key}`}
                />
              )}
            </div>
          ))}
        </div>

        <div className="px-6 py-4 border-t border-white/[0.06] flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-[12px] font-semibold text-[#9CA3AF] hover:text-white transition-all" data-testid="setup-cancel">Cancel</button>
          <button
            onClick={submit}
            disabled={saving}
            className="px-5 py-2 rounded-lg bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] text-white text-[12px] font-bold uppercase tracking-wider hover:shadow-[0_0_24px_rgba(139,92,246,0.4)] disabled:opacity-40 transition-all flex items-center gap-1.5"
            data-testid="setup-submit"
          >
            {saving ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
            {submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
